import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowRight, FaLock, FaUserShield } from 'react-icons/fa';
import { authAPI } from '../services/api';
import { formatApiError } from '../utils/errorUtils';
import logo from '../assets/logo.png';
import './AuthPages.css';

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const requestedType = String(searchParams.get('type') || '').toLowerCase();
  const type = requestedType === 'admin' || requestedType === 'staff' ? 'admin' : 'user';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const title = useMemo(() => (type === 'admin' ? 'Reset Admin Password' : 'Reset User Password'), [type]);
  const backLink = type === 'admin' ? '/admin/login' : '/login';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!token) {
      setError('Reset link is missing or invalid.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.resetPassword({
        token,
        password,
        confirmPassword,
        accountType: type
      });
      setMessage(response.data?.message || 'Password reset successful.');
      setTimeout(() => {
        navigate(backLink);
      }, 1200);
    } catch (err) {
      setError(formatApiError(err, 'Failed to reset password'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-shell auth-container">
      <div className="blur-circle circle-1" />
      <div className="blur-circle circle-2" />

      <motion.div
        className="auth-card-modern teacher-theme"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="auth-header-admin">
          <div className="admin-badge">
            <FaUserShield /> <span>Password Reset</span>
          </div>
          <div className="auth-brand-block">
            <img src={logo} alt="Epsilon logo" className="auth-brand-logo" />
            <div>
              <h2>{title}</h2>
              <p>Choose a new password for your EPSILON account.</p>
            </div>
          </div>
        </div>

        {error && <div className="error-toast">{error}</div>}
        {message && <div className="success-toast">{message}</div>}

        <form onSubmit={handleSubmit} className="modern-form">
          <div className="input-container">
            <label><FaLock /> New Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              required
              disabled={loading}
            />
          </div>

          <div className="input-container">
            <label><FaLock /> Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              disabled={loading}
            />
          </div>

          <button type="submit" className="btn-admin-login" disabled={loading}>
            {loading ? 'Resetting...' : <>Save New Password <FaArrowRight /></>}
          </button>
        </form>

        <div className="auth-footer-links">
          <Link to={backLink} className="back-link">Back to Login</Link>
          <Link to="/" className="back-link">Return Home</Link>
        </div>
      </motion.div>
    </div>
  );
}

export default ResetPasswordPage;
